import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Building2,
  ChevronRight,
  Loader2,
  Plus,
  Trash2,
  Users,
  UsersRound,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import TeamFormDialog from "@/components/teams/TeamFormDialog";
import { useAuth } from "@/contexts/auth.context";
import { toast } from "@/hooks/use-toast";
import { ehAcessoNegado, mensagemDeErro } from "@/lib/team-errors";
import teamService, { type TeamItem } from "@/services/team.service";

/**
 * A tela de **times da agência**, que tem duas faces conforme quem abre.
 *
 * **Quem administra** vê a gestão: a lista dos times da agência, com as contagens de
 * membros e de lojas, e os caminhos para criar, editar, desativar e excluir.
 *
 * **O membro** vê os times de que faz parte, com o caminho para o painel consolidado de
 * cada um. Ele não tem o que gerir aqui — `GET /api/v1/teams` responde `403` para ele —, e
 * a lista dele vem do escopo que o contexto de autenticação já carregou (`my-scope`), sem
 * nenhuma chamada nova.
 *
 * As duas faces convivem: um funcionário com papel `admin` na conta principal administra os
 * times **e** pode ser membro de um deles.
 */
const TeamsPage = () => {
  const navigate = useNavigate();
  const { canManageTeams, isTeamMember, teamScope, loading: perfilCarregando } =
    useAuth();

  const [teams, setTeams] = useState<TeamItem[]>([]);
  const [loading, setLoading] = useState(canManageTeams);
  const [semPermissao, setSemPermissao] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [emEdicao, setEmEdicao] = useState<TeamItem | null>(null);
  const [aExcluir, setAExcluir] = useState<TeamItem | null>(null);
  const [excluindo, setExcluindo] = useState(false);

  const carregar = useCallback(async () => {
    if (!canManageTeams) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setTeams(await teamService.list());
      setSemPermissao(false);
    } catch (error) {
      // O `403` aqui não é falha: é a resposta a quem não administra a agência, e a tela
      // diz isso em vez de mostrar uma lista vazia que pareceria "não há times".
      if (ehAcessoNegado(error)) {
        setSemPermissao(true);
      } else {
        toast({
          title: "Erro ao carregar os times",
          description: mensagemDeErro(error, "Tente novamente."),
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [canManageTeams]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    carregar();
  }, [carregar]);

  const excluir = async () => {
    if (!aExcluir) return;
    setExcluindo(true);
    try {
      await teamService.remove(aExcluir.id);
      toast({ title: `Time "${aExcluir.name}" excluído.` });
      setAExcluir(null);
      await carregar();
    } catch (error) {
      toast({
        title: "Erro ao excluir o time",
        description: mensagemDeErro(error, "Tente novamente."),
        variant: "destructive",
      });
    } finally {
      setExcluindo(false);
    }
  };

  const abrir = (teamId: string) => navigate(`/dashboard/teams/${teamId}`);

  // Enquanto o perfil não chegou não se sabe qual das duas faces é a certa, e mostrar a do
  // membro para quem administra seria um piscar de "você não faz parte de nenhum time".
  if (perfilCarregando) {
    return (
      <div className="space-y-4 p-4 sm:p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  /* ─────────────── a face do membro ─────────────── */

  if (!canManageTeams) {
    const meusTimes = teamScope?.teams ?? [];

    return (
      <div className="p-4 sm:p-6 space-y-6">
        <header>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            <UsersRound className="h-8 w-8" />
            Meus times
          </h1>
          <p className="text-muted-foreground">
            Os times de que você faz parte e as lojas que eles atendem
          </p>
        </header>

        {!isTeamMember ? (
          <Card>
            <CardContent className="py-12 text-center">
              <UsersRound className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
              <p className="font-medium">Você não faz parte de nenhum time.</p>
              <p className="text-sm text-muted-foreground">
                Quem administra a agência pode incluir você em um time para dar
                acesso às lojas que lhe couberem.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {meusTimes.map((time) => (
              <Card key={time.id}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UsersRound className="h-5 w-5" />
                    {time.name}
                  </CardTitle>
                  {time.description && (
                    <CardDescription>{time.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <Button
                    variant="outline"
                    onClick={() => abrir(time.id)}
                    className="w-full sm:w-auto"
                  >
                    Painel consolidado
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {isTeamMember && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Building2 className="h-4 w-4" />
                Lojas que você alcança
              </CardTitle>
              <CardDescription>
                Troque de loja pelo seletor no menu lateral. Seu acesso a elas é
                somente leitura.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {/* A loja sem `customer_uuid` não tem a quem pedir dados, e é por isso que
                    o seletor não a oferece. Ela continua listada aqui — o time realmente a
                    atende —, mas marcada, para que ninguém a procure no seletor e ache que
                    sumiu. */}
                {(teamScope?.accounts ?? []).map((loja) => (
                  <Badge
                    key={loja.account_id}
                    variant="outline"
                    className={loja.customer_uuid ? "" : "text-muted-foreground"}
                  >
                    {loja.name}
                    {!loja.customer_uuid && " · sem dados"}
                  </Badge>
                ))}
                {(teamScope?.accounts.length ?? 0) === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Nenhuma loja foi atribuída aos seus times ainda.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  /* ─────────────── a face de quem administra ─────────────── */

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            <UsersRound className="h-8 w-8" />
            Times
          </h1>
          <p className="text-muted-foreground">
            Dê a um grupo de funcionários da agência as lojas que lhe cabem
          </p>
        </div>
        {!semPermissao && (
          <Button
            onClick={() => {
              setEmEdicao(null);
              setShowForm(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Novo time
          </Button>
        )}
      </header>

      {semPermissao ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="mx-auto mb-3 h-10 w-10 text-amber-500" />
            <p className="font-medium">
              Você não administra os times desta agência.
            </p>
            <p className="text-sm text-muted-foreground">
              Apenas o titular da assinatura ou um administrador da conta
              principal podem gerir times.
            </p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : teams.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <UsersRound className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
            <p className="font-medium">Nenhum time por aqui ainda.</p>
            <p className="mb-4 text-sm text-muted-foreground">
              Um time reúne funcionários da agência e as lojas que eles
              acompanham.
            </p>
            <Button
              onClick={() => {
                setEmEdicao(null);
                setShowForm(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Criar o primeiro time
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {teams.map((time) => (
            <Card key={time.id} className={time.is_active ? "" : "opacity-70"}>
              <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={() => abrir(time.id)}
                  className="min-w-0 flex-1 text-left"
                >
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">{time.name}</span>
                    {!time.is_active && (
                      <Badge variant="outline" className="shrink-0">
                        Inativo
                      </Badge>
                    )}
                  </div>
                  {time.description && (
                    <p className="truncate text-sm text-muted-foreground">
                      {time.description}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <Users className="h-4 w-4" />
                      {time.member_count}{" "}
                      {time.member_count === 1 ? "membro" : "membros"}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Building2 className="h-4 w-4" />
                      {time.account_count}{" "}
                      {time.account_count === 1 ? "loja" : "lojas"}
                    </span>
                  </div>
                </button>

                <div className="flex shrink-0 items-center gap-2">
                  <Button variant="outline" onClick={() => abrir(time.id)}>
                    Abrir
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEmEdicao(time);
                      setShowForm(true);
                    }}
                  >
                    Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Excluir o time ${time.name}`}
                    onClick={() => setAExcluir(time)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TeamFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        team={emEdicao}
        onSaved={carregar}
      />

      <AlertDialog
        open={aExcluir !== null}
        onOpenChange={(aberto) => !aberto && setAExcluir(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir o time?</AlertDialogTitle>
            <AlertDialogDescription>
              O time <strong>{aExcluir?.name}</strong> e a composição dele são
              apagados. Os funcionários e as lojas continuam existindo — o que
              se perde é o acesso que o time dava. Para tirar o acesso sem
              apagar nada, desative o time em vez de excluí-lo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={excluindo}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                excluir();
              }}
              disabled={excluindo}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {excluindo && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TeamsPage;
